import { useTranslation } from "react-i18next";
import { Box, Divider, Skeleton, Typography } from "@mui/material";

import { Service } from "../../../../types/service.type";
import {
  IdealCompletionTimeIcon,
  NumberOfVideosIcon,
  ProgramLevelIcon,
} from "../../../../assets/icons/service-details-icons";

const ProgramDetail = ({
  icon: Icon,
  label,
  value,
  isLoading,
}: {
  icon: React.FC;
  label: string;
  value: string;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", gap: "16px" }}>
        <Skeleton variant="circular" width={24} height={24} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Icon />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography sx={styles.detailLabel}>{label}</Typography>
        <Typography sx={styles.detailValue}>{value}</Typography>
      </Box>
    </Box>
  );
};

const ServiceDetails = ({ service, isLoading }: { service?: Service; isLoading?: boolean }) => {
  const { t } = useTranslation("goService");
  const idealCompletionTime = service?.idealCompletionTime;
  const numberOfVideos = service?.numberOfVideos;
  const programLevel = service?.level;

  return (
    <Box sx={styles.detailsContainer}>
      {(isLoading || idealCompletionTime) && (
        <ProgramDetail
          icon={IdealCompletionTimeIcon}
          label={t("servicePage.idealProgramFinishesIn")}
          value={t("servicePage.month", { count: idealCompletionTime })}
          isLoading={isLoading}
        />
      )}

      {(isLoading || (idealCompletionTime && numberOfVideos)) && <Divider />}

      {(isLoading || numberOfVideos) && (
        <ProgramDetail
          icon={NumberOfVideosIcon}
          label={t("servicePage.numberOfVideos")}
          value={t("servicePage.video", { count: numberOfVideos })}
          isLoading={isLoading}
        />
      )}

      {(isLoading || (programLevel && numberOfVideos)) && <Divider />}

      {(isLoading || programLevel) && (
        <ProgramDetail
          icon={ProgramLevelIcon}
          label={t("servicePage.programLevel")}
          value={t(`programLevel.${programLevel}`)}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
};

export default ServiceDetails;

const styles = {
  categoryChip: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    borderRadius: "16px",
    background: "var(--Green---25, #E7EFED)",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "16px",
  },
  detailLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  detailValue: {
    fontFamily: "Montserrat",
    color: "var(--black-100, #1C1C1C)",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "16px",
  },
} as const;
